import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiJson } from '../lib/api'
import { type AppRole } from '../lib/auth'
import ComingSoon from '../components/ComingSoon'
import AgentDashboard, { type AgentDashboardData } from '../components/dashboard/AgentDashboard'
import CoachDashboard, { type CoachDashboardData } from '../components/dashboard/CoachDashboard'

type State =
  | { kind: 'loading' }
  | { kind: 'unauthenticated' }
  | { kind: 'role_missing' }
  | { kind: 'agent_setup_required' }
  | { kind: 'coach_setup_required' }
  | { kind: 'agent_ready'; data: AgentDashboardData }
  | { kind: 'coach_ready'; data: CoachDashboardData }
  | { kind: 'player_role' }

interface MeResponse {
  profile: { role: AppRole | null }
}

export default function Dashboard() {
  const navigate = useNavigate()
  const [state, setState] = useState<State>({ kind: 'loading' })

  useEffect(() => {
    let cancelled = false
    async function resolve() {
      let role: AppRole | null
      try {
        const me = await apiJson<MeResponse>('/api/me')
        if (cancelled) return
        role = me.profile.role
      } catch (e) {
        if (cancelled) return
        const msg = e instanceof Error ? e.message : 'unknown_error'
        if (msg === 'unauthorized') setState({ kind: 'unauthenticated' })
        else setState({ kind: 'unauthenticated' })
        return
      }

      if (role === null) {
        setState({ kind: 'role_missing' })
        return
      }
      if (role === 'player') {
        setState({ kind: 'player_role' })
        return
      }
      if (role === 'admin') {
        // No admin dashboard in v1 — fall through to player-style ComingSoon.
        setState({ kind: 'player_role' })
        return
      }

      if (role === 'agent') {
        try {
          const data = await apiJson<AgentDashboardData>('/api/dashboard/agent')
          if (cancelled) return
          setState({ kind: 'agent_ready', data })
        } catch (e) {
          if (cancelled) return
          const msg = e instanceof Error ? e.message : 'unknown_error'
          if (msg === 'agent_setup_required') setState({ kind: 'agent_setup_required' })
          else setState({ kind: 'unauthenticated' })
        }
        return
      }

      if (role === 'coach') {
        try {
          const data = await apiJson<CoachDashboardData>('/api/dashboard/coach')
          if (cancelled) return
          if (!data.coach) {
            setState({ kind: 'coach_setup_required' })
            return
          }
          setState({ kind: 'coach_ready', data })
        } catch (e) {
          if (cancelled) return
          const msg = e instanceof Error ? e.message : 'unknown_error'
          if (msg === 'role_mismatch') setState({ kind: 'role_missing' })
          else setState({ kind: 'unauthenticated' })
        }
        return
      }
    }
    resolve()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    if (state.kind === 'unauthenticated') navigate('/', { replace: true })
    if (state.kind === 'role_missing') navigate('/onboarding', { replace: true })
    if (state.kind === 'agent_setup_required') navigate('/onboarding/agent', { replace: true })
    if (state.kind === 'coach_setup_required') navigate('/onboarding/coach', { replace: true })
  }, [state, navigate])

  if (
    state.kind === 'loading' ||
    state.kind === 'unauthenticated' ||
    state.kind === 'role_missing' ||
    state.kind === 'agent_setup_required' ||
    state.kind === 'coach_setup_required'
  ) {
    return <div className="max-w-3xl mx-auto px-6 py-12 text-hoop-text-3 text-sm">Loading…</div>
  }
  if (state.kind === 'player_role') {
    return <ComingSoon title="Player Dashboard" subtitle="Coming in a later milestone" />
  }
  if (state.kind === 'coach_ready') {
    return <CoachDashboard data={state.data} />
  }
  return <AgentDashboard data={state.data} />
}
